import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import RequestSyllabusModal from "./RequestSyllabusModal";
import { addToDataLayer } from "@website-builder/utilities-index";
import { SecondaryNavWrapper } from "./styles";
import { Button, CtaButton, InterestedContactForm } from "../../Elements";
import {
	throttle,
	scrollToElement,
	GetReferrer,
} from "@website-builder/utilities-index";
import { getVwoCampaignIdFromWindow } from "@website-builder/utilities-index";

const AElement = (props) => React.createElement("a", props);
const STICKY_MARGIN = 80;
const VWO_CAMPAIGN_DATA = getVwoCampaignIdFromWindow();

const SecondaryNavigation = (props) => {
	// States & Ref
	const secondaryNavEl = useRef(null);
	const [show, setShow] = useState(false);
	const [sticky, setSticky] = useState(false);
	const [initialTop, setInitialTop] = useState(null);

	const {
		hashLinks,
		context,
		globalDomain,
		portalId,
		formId,
		cta,
		navigationWithoutCTA,
		requestFormType,
		pageSection,
	} = props;

	const { slug } = context;

	const onSyllabusRequested = (phoneNumber) => {
		const [referral, referral2] = GetReferrer();
		//Trigger GA4 and Segment Event through GTM
		addToDataLayer({
			event: "syllabus_download",
			page_section: pageSection,
			course_slug: slug,
			phone_no: phoneNumber,
			referral,
			referral2,
		});
	};

	// Initialize the initial top property of Secondary Nav once the dom element is mounted
	useEffect(() => {
		window.requestAnimationFrame(() => {
			window.scrollTo(0, 0);
			const top = secondaryNavEl.current.getBoundingClientRect().top;
			setInitialTop(top);
		});
	}, [secondaryNavEl, setInitialTop]);

	// Listen to scroll event and toggle the sticky behaviour of Secondary Navigation
	useEffect(() => {
		const makeElementSticky = throttle(() => {
			window.requestAnimationFrame(() => {
				const top = secondaryNavEl.current.getBoundingClientRect().top;
				const scrollY = window.scrollY;

				if (sticky) {
					if (initialTop - scrollY >= STICKY_MARGIN) {
						setSticky(false);
					}
				} else {
					if (top <= STICKY_MARGIN) {
						setSticky(true);
					}
				}
			});
		}, 100);

		window.addEventListener("scroll", makeElementSticky);

		return () => {
			window.removeEventListener("scroll", makeElementSticky);
		};
	}, [initialTop, sticky]);

	return (
		<SecondaryNavWrapper ref={secondaryNavEl} sticky={sticky}>
			<div className="stickyWrapper">
				<div className="linksWrapper">
					{hashLinks
						.filter((hl) => !!hl.label && !!hl.link && !!hl.link.value)
						.map(({ label, link: { value: hash } }, index) => (
							<a
								onClick={() => scrollToElement(hash)}
								key={`${label}-${index}`}
							>
								{label}
							</a>
						))}
				</div>
				{!navigationWithoutCTA && (
					<div className="ctaWrapper">
						{requestFormType === "emailForm" && (
							<>
								<Button type="outline" onClick={() => setShow(true)}>
									Request syllabus
								</Button>
								<RequestSyllabusModal
									context={context}
									globalDomain={globalDomain}
									portalId={portalId}
									formId={formId}
									show={show}
									variation_id={VWO_CAMPAIGN_DATA}
									onSyllabusRequested={() => onSyllabusRequested(null)}
									onClose={() => setShow(false)}
								/>
							</>
						)}

						{requestFormType === "contactForm" && (
							<>
								<InterestedContactForm
									apiEndPoint={`${context.apiBaseUrl}/learning-paths/interested/`}
									formData={{
										workshop_landing_page_request: 1,
										workshop_id: context.workshop_id,
										syllabus: 1,
										js_disabled: "yes",
										drip_tag_prefix: context.dripTagPrefix,
										variation_id: VWO_CAMPAIGN_DATA,
									}}
									actionOnSuccess="SUCCESS_MESSAGE_AND_CALLBACK"
									callbackOnSuccess={(data) =>
										onSyllabusRequested(data.phone_no)
									}
									callbackOnError={() => {}}
									successMessage={"Syllabus is sent to your email"}
									className={"request-syllabus-form"}
								>
									<Button type="outline">Request syllabus</Button>
								</InterestedContactForm>
							</>
						)}
						<CtaButton cta={cta} />
					</div>
				)}
			</div>
		</SecondaryNavWrapper>
	);
};

SecondaryNavigation.defaultProps = {
	hashLinks: [],

	externalDependency: {
		Link: AElement,
	},
	context: {
		fullSlug: "",
	},
	navigationWithoutCTA: false,
	requestFormType: "emailForm",
};

const HashLink = PropTypes.shape({
	type: PropTypes.oneOf(["hash"]).isRequired,
	value: PropTypes.string.isRequired,
});

SecondaryNavigation.propTypes = {
	hashLinks: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			link: HashLink,
		}),
	),
	externalDependency: PropTypes.shape({
		Link: PropTypes.func,
	}),
	context: PropTypes.shape({
		fullSlug: PropTypes.string,
	}),
	navigationWithoutCTA: PropTypes.bool,
	requestFormType: PropTypes.oneOf(["emailForm", "contactForm"]),
};

export default SecondaryNavigation;
